import { Badge } from "@/components/ui/badge";
import { rupiah, api, slugify } from "@/lib/api";
import { Link } from "react-router-dom";
import { Star, ShieldCheck, Zap, Clock, Share2 } from "lucide-react";
import { shareScheduleToWA } from "@/lib/shareSchedule";

const STATUS = {
  available: { label: "TERSEDIA", cls: "text-emerald-400" },
  full: { label: "PENUH", cls: "text-red-400" },
  cancelled: { label: "DIBATALKAN", cls: "text-zinc-500" },
  departed: { label: "SUDAH BERANGKAT", cls: "text-zinc-500" },
};

export const DepartureBoard = ({ schedules, onBook, showRoute = true }) => {
  if (!schedules?.length) {
    return (
      <div className="border border-border bg-card p-10 text-center text-muted-foreground" data-testid="terminal-empty">
        Belum ada jadwal keberangkatan. Coba ubah pencarian atau tanggal.
      </div>
    );
  }

  return (
    <div className="overflow-hidden border border-border bg-[#0b0b0d]" data-testid="departure-board">
      {/* header */}
      <div className="hidden grid-cols-12 gap-2 border-b border-border bg-black px-4 py-3 font-mono-terminal text-[11px] uppercase tracking-widest text-muted-foreground md:grid">
        <div className="col-span-2">Jam</div>
        <div className="col-span-4">Rute</div>
        <div className="col-span-2">Driver</div>
        <div className="col-span-2 text-right">Harga</div>
        <div className="col-span-2 text-right">Kursi / Status</div>
      </div>

      {schedules.map((s, i) => {
        const st = STATUS[s.status] || STATUS.available;
        const disabled = s.status !== "available" || s.seats_left <= 0;
        return (
          <div
            key={s.id}
            data-testid={`schedule-row-${s.id}`}
            className="flex animate-flip-in flex-col gap-1.5 border-b border-border px-4 py-4 transition-colors hover:bg-white/[0.03] md:grid md:grid-cols-12 md:items-center md:gap-2"
            style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}
          >
            <div className="col-span-1 md:col-span-2">
              <div className="font-mono-terminal text-2xl font-bold tracking-tight text-primary">{s.time}</div>
              {s.date && <div className="font-mono-terminal text-[11px] text-muted-foreground" data-testid={`date-${s.id}`}>{s.date.split("-").reverse().join("/")}</div>}
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-display text-base font-bold uppercase tracking-tight break-words md:text-lg" data-testid={`route-${s.id}`}>
                  {s.origin} <span className="text-primary">→</span> {s.destination}
                </span>
                {s.sponsor && (
                  <Badge className="h-5 bg-primary/20 px-1.5 text-[9px] font-bold text-primary hover:bg-primary/20">
                    <Zap className="mr-0.5 h-2.5 w-2.5" /> SPONSOR
                  </Badge>
                )}
                {!s.sponsor && s.top_rated && (
                  <Badge className="h-5 bg-emerald-500/20 px-1.5 text-[9px] font-bold text-emerald-400 hover:bg-emerald-500/20" data-testid={`top-rated-${s.id}`}>
                    <Star className="mr-0.5 h-2.5 w-2.5 fill-emerald-400" /> RATING TERTINGGI
                  </Badge>
                )}
                {s.departing_soon && (
                  <Badge className="h-5 animate-pulse bg-orange-500/20 px-1.5 text-[9px] font-bold text-orange-400 hover:bg-orange-500/20" data-testid={`departing-soon-${s.id}`}>
                    <Clock className="mr-0.5 h-2.5 w-2.5" /> SEGERA
                  </Badge>
                )}
              </div>
              {(s.waypoints || []).length > 0 && (
                <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`waypoints-${s.id}`}>
                  <span className="text-primary">Lewat:</span> {s.waypoints.map((w) => (w.time ? `${w.city} (${w.time})` : w.city)).join(" · ")}
                </div>
              )}
            </div>
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-1 text-sm">
                <Link to={`/driver/${slugify(s.driver_name)}`} onClick={(e) => e.stopPropagation()} className="truncate hover:text-primary hover:underline" data-testid={`driver-link-${s.driver_id}`}>{s.driver_name}</Link>
                {s.driver_verified && <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400" />}
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Star className="h-3 w-3 fill-primary text-primary" /> {s.driver_rating || "Baru"}
                {s.driver_reviews > 0 && <span className="text-[10px]">({s.driver_reviews})</span>}
                {s.driver_priority && <span className="ml-1 text-primary">· Prioritas</span>}
              </div>
            </div>
            <div className="col-span-1 md:col-span-2 md:text-right">
              <div className="font-mono-terminal text-lg font-bold">{rupiah(s.price)}</div>
              <div className="text-[10px] text-muted-foreground">{s.service_type === "charter" ? "/ mobil (carter)" : "/ penumpang"}</div>
            </div>
            <div className="col-span-1 flex flex-row items-center gap-3 md:col-span-2 md:flex-col md:items-end md:gap-1">
              <span className={`font-mono-terminal text-xs font-bold ${st.cls}`}>
                {s.status === "available" ? `${s.seats_left} KURSI` : st.label}
              </span>
              <button
                data-testid={`book-btn-${s.id}`}
                disabled={disabled}
                onClick={() => { if (s.sponsor) api.post(`/ads/${s.id}/click`).catch(() => {}); onBook(s); }}
                className="rounded-md bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-30"
              >
                {disabled ? "Penuh" : "Pesan"}
              </button>
              <button
                data-testid={`share-btn-${s.id}`}
                onClick={() => shareScheduleToWA(s)}
                title="Bagikan jadwal ke WhatsApp"
                className="flex items-center gap-1 text-[11px] text-[#25D366] transition-colors hover:text-[#25D366]/80 hover:underline"
              >
                <Share2 className="h-3 w-3" /> Bagikan
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
